"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AccessTokenManager = exports.ACCESS_STORAGE_KEY = void 0;
const errors_1 = require("../errors");
const logger_1 = require("../utils/logger");
const sdk_user_1 = require("../utils/sdk_user");
exports.ACCESS_STORAGE_KEY = 'STREEM_ACCESS_TOKEN';
let localAccessToken = null;
// Expire the access token a few seconds earlier to account for network+code latency.
const TokenExpirationSlop = 10;
class AccessTokenManager {
    constructor(apiUrl, appId) {
        this.apiUrl = apiUrl;
        this.appId = appId;
    }
    getExistingTokenIfValid() {
        const storedToken = this.retrieveFromStorage();
        if (storedToken && storedToken.expiresAt > Date.now() / 1000) {
            logger_1.logger.debug('Found a stored access token that is still valid');
            return storedToken.token;
        }
    }
    fetch(idOrSdkToken, companyCode) {
        return __awaiter(this, void 0, void 0, function* () {
            // Attempt to retrieve a cached access token from storage.
            const existingToken = this.getExistingTokenIfValid();
            if (existingToken)
                return existingToken;
            const storedToken = this.retrieveFromStorage();
            if (storedToken) {
                logger_1.logger.info('Found a stored access token but it has expired, so refreshing it');
                try {
                    return yield this.refresh(companyCode);
                }
                catch (error) {
                    // SDK Tokens (aka Embedded SSO) doesn't support Refresh Tokens, so info() in that case, otherwise error()
                    const loggerMethod = (0, sdk_user_1.isSDKUserToken)(idOrSdkToken) ? 'info' : 'error';
                    logger_1.logger[loggerMethod](`Access token is expired and we were unable to refresh the token for company code: ${companyCode}, user id: ${storedToken.token.streem_user_id}`, error);
                }
            }
            logger_1.logger.info('Could not find a stored access token, so requesting one from /auth/token');
            let scope = 'streem:user';
            if ((0, sdk_user_1.isSDKUserToken)(idOrSdkToken)) {
                scope += ' streem:sdk';
            }
            const oauthCredentials = yield this.requestAccessToken({
                assertion: idOrSdkToken,
                grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
                scope,
                client_id: this.appId,
                streem_company_code: companyCode,
                audience: `${this.apiUrl}/`,
            });
            this.storeAccessToken(oauthCredentials);
            return oauthCredentials;
        });
    }
    /**
     * Requests a new token using the current token's `refresh_token`
     */
    refresh(companyCode) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const storedOldToken = this.retrieveFromStorage();
            if (!storedOldToken || !storedOldToken.token.refresh_token) {
                throw new Error('Failed to refresh token: credentials do not exist in storage');
            }
            let accessToken = yield this.requestAccessToken({
                refresh_token: storedOldToken.token.refresh_token,
                grant_type: `refresh_token`,
                client_id: this.appId,
                streem_company_code: companyCode,
            });
            // The new token we receive when using a refresh token
            // might not have a new refresh_token. If it does not have
            // a new refresh token, we should use the old token
            accessToken = Object.assign(Object.assign({}, accessToken), { refresh_token: (_a = accessToken.refresh_token) !== null && _a !== void 0 ? _a : storedOldToken.token.refresh_token });
            this.storeAccessToken(accessToken);
            return accessToken;
        });
    }
    /**
     * Removes token from session storage
     */
    clearStorage() {
        logger_1.logger.info('Removing access token from sessionStorage');
        sessionStorage.removeItem(exports.ACCESS_STORAGE_KEY);
        localAccessToken = null;
    }
    retrieveFromStorage() {
        let rawAccessToken = sessionStorage.getItem(exports.ACCESS_STORAGE_KEY);
        if (!rawAccessToken) {
            if (localAccessToken) {
                logger_1.logger.warn('Unable to find access token in sessionStorage, but did find in local variable... using local variable');
                rawAccessToken = localAccessToken;
            }
            else {
                return;
            }
        }
        try {
            return JSON.parse(rawAccessToken);
        }
        catch (error) {
            // We failed to parse the JSON, which means our token is corrupted.
            logger_1.logger.error('Failed to parse access token from SessionStorage.', error);
            this.clearStorage();
            return;
        }
    }
    /**
     * Stores a token in session storage
     * @param accessToken The token to be stored
     */
    storeAccessToken(accessToken) {
        const token = JSON.stringify({
            expiresAt: Date.now() / 1000 + accessToken.expires_in - TokenExpirationSlop,
            token: accessToken,
        });
        sessionStorage.setItem(exports.ACCESS_STORAGE_KEY, token);
        localAccessToken = token;
    }
    /**
     * Requests access_token
     * @param body PostOAuthCredentialsBody payload
     */
    requestAccessToken(body) {
        return __awaiter(this, void 0, void 0, function* () {
            const bodyParams = new URLSearchParams();
            for (const key in body) {
                if (Object.prototype.hasOwnProperty.call(body, key)) {
                    const value = body[key];
                    if (value) {
                        bodyParams.append(key, value);
                    }
                }
            }
            const response = yield fetch(`${this.apiUrl}/auth/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: bodyParams,
            });
            const { status } = response;
            if (status >= 200 && status < 300) {
                const accessToken = yield response.json();
                return accessToken;
            }
            else if (status >= 400 && status < 500) {
                throw new errors_1.UnauthenticatedError(`Received error.status=${response.status} error.responseBody=<${yield response.text()}>  error.url=<${response.url}>`, response.status);
            }
            else {
                throw new errors_1.LoginError(`Received error error.status=${response.status} error.responseBody=<${yield response.text()}>  error.url=<${response.url}>`, response.status);
            }
        });
    }
    invitationLogin(inviteCode) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.getInvitationAccessToken(inviteCode);
        });
    }
    getInvitationAccessToken(inviteCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.apiUrl}/v1/auth/invitation`, {
                method: 'POST',
                body: JSON.stringify({
                    invitation_code: inviteCode,
                    app_sid: this.appId,
                    resource: this.apiUrl,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            logger_1.logger.info(`Attempted to fetch access token using an invitation code, status: ${response.status}`);
            if (!response.ok) {
                switch (response.status) {
                    case 401:
                        throw new errors_1.LoginError('Invalid login', response.status);
                    case 403:
                        throw new errors_1.LoginError('Referrer header does not match', response.status);
                    default:
                        throw new errors_1.LoginError('Unknown authentication error.', response.status);
                }
            }
            const invitation = yield response.json();
            logger_1.logger.info(`Successfully received invitation access token.  Attempting to store in sessionStorage (available: ${storageAvailable('sessionStorage')})`);
            this.storeAccessToken(invitation);
            const retrievedValue = this.retrieveFromStorage();
            const rawValue = sessionStorage.getItem(exports.ACCESS_STORAGE_KEY);
            logger_1.logger.info(`After storing invitation access token in sessionStorage, raw value is ` +
                `type: ${typeof rawValue}, token value is type: ${typeof retrievedValue}`);
            return invitation;
        });
    }
}
exports.AccessTokenManager = AccessTokenManager;
// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#testing_for_availability
function storageAvailable(type) {
    let storage;
    try {
        storage = window[type];
        const x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    }
    catch (e) {
        return (e instanceof DOMException &&
            // everything except Firefox
            (e.code === 22 ||
                // Firefox
                e.code === 1014 ||
                // test name field too, because code might not be present
                // everything except Firefox
                e.name === 'QuotaExceededError' ||
                // Firefox
                e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            storage &&
            storage.length !== 0);
    }
}
//# sourceMappingURL=access_token_manager.js.map